import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileImage, FileText, FileSpreadsheet, Link, Video, ExternalLink, Eye, Music } from "lucide-react";
import type { ModuleContent as ModuleContentType } from "@/hooks/useCourses";
import { DocumentViewerDialog } from "./DocumentViewerDialog";
import { getStorageUrl } from "@/lib/storage";

interface ModuleContentProps {
  content: ModuleContentType[];
}

export function ModuleContent({ content }: ModuleContentProps) {
  if (!content.length) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">No content available for this module yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {content.map((item) => (
        <ContentItem key={item.id} item={item} />
      ))}
    </div>
  );
}

function ContentItem({ item }: { item: ModuleContentType }) {
  // Resolve content URL if it's a storage path
  const resolvedUrl = item.content_url ? (
    item.content_url.startsWith("http") 
      ? item.content_url 
      : getStorageUrl("course-content", item.content_url)
  ) : null;

  switch (item.content_type) {
    case "video":
      return <VideoContent url={resolvedUrl} displayTitle={item.display_title} />;
    case "text":
      return <TextContent text={item.content_text} />;
    case "image":
      return <ImageContent url={resolvedUrl} displayTitle={item.display_title} />;
    case "document":
      return <DocumentContent url={resolvedUrl} displayTitle={item.display_title} />;
    case "link":
      return <LinkContent url={item.content_url} />;
    case "audio":
      return <AudioContent url={resolvedUrl} displayTitle={item.display_title} />;
    default:
      return null;
  }
}

function VideoContent({ url, displayTitle }: { url: string | null; displayTitle?: string | null }) {
  if (!url) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Video className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">Video not available</p>
        </CardContent>
      </Card>
    );
  }

  // Handle YouTube embeds
  const youtubeMatch = url.match(
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
  );

  if (youtubeMatch) {
    return (
      <div className="space-y-2">
        {displayTitle && (
          <h3 className="font-medium text-lg">{displayTitle}</h3>
        )}
        <div className="aspect-video rounded-lg overflow-hidden bg-muted">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeMatch[1]}`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={displayTitle || "Video content"}
          />
        </div>
      </div>
    );
  }

  // Handle Vimeo embeds
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return (
      <div className="space-y-2">
        {displayTitle && (
          <h3 className="font-medium text-lg">{displayTitle}</h3>
        )}
        <div className="aspect-video rounded-lg overflow-hidden bg-muted">
          <iframe
            src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
            className="w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={displayTitle || "Video content"}
          />
        </div>
      </div>
    );
  }

  // Direct video URL
  return (
    <div className="space-y-2">
      {displayTitle && (
        <h3 className="font-medium text-lg">{displayTitle}</h3>
      )}
      <div className="aspect-video rounded-lg overflow-hidden bg-muted">
        <video src={url} controls className="w-full h-full">
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  );
}

function TextContent({ text }: { text: string | null }) {
  if (!text) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No text content</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="py-6">
        <div className="prose prose-sm max-w-none dark:prose-invert">
          {text.split("\n").map((paragraph, idx) => (
            <p key={idx}>{paragraph}</p>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ImageContent({ url, displayTitle }: { url: string | null; displayTitle?: string | null }) {
  if (!url) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <FileImage className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">Image not available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {displayTitle && (
        <h3 className="font-medium text-lg">{displayTitle}</h3>
      )}
      <div className="rounded-lg overflow-hidden">
        <img src={url} alt={displayTitle || "Course content"} className="w-full h-auto" />
      </div>
    </div>
  );
}

function DocumentContent({ url, displayTitle }: { url: string | null; displayTitle?: string | null }) {
  const [viewerOpen, setViewerOpen] = useState(false);

  if (!url) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <FileSpreadsheet className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">Document not available</p>
        </CardContent>
      </Card>
    );
  }

  // Extract filename from URL for fallback and file type detection
  const urlFileName = url.split('/').pop()?.split('?')[0] || 'Document';
  const fileExt = urlFileName.split('.').pop()?.toLowerCase() || '';
  
  // Use display_title if available, otherwise extract from URL
  const displayName = displayTitle || urlFileName.replace(/\.[^/.]+$/, "");
  
  // Determine file type for display
  const getFileTypeLabel = (ext: string) => {
    switch (ext) {
      case 'pdf': return 'PDF Document';
      case 'doc':
      case 'docx': return 'Word Document';
      case 'xls':
      case 'xlsx': return 'Excel Spreadsheet';
      case 'jpg':
      case 'jpeg': return 'JPEG Image';
      case 'png': return 'PNG Image';
      default: return 'Document';
    }
  };

  const getFileIcon = () => {
    if (['jpg', 'jpeg', 'png'].includes(fileExt)) {
      return <FileImage className="h-10 w-10 text-primary" />;
    }
    if (fileExt === 'pdf') {
      return <FileText className="h-10 w-10 text-primary" />;
    }
    return <FileSpreadsheet className="h-10 w-10 text-primary" />;
  };

  return (
    <>
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {getFileIcon()}
              <div>
                <p className="font-medium">{displayName}</p>
                <p className="text-sm text-muted-foreground">{getFileTypeLabel(fileExt)}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => setViewerOpen(true)}>
              <Eye className="h-4 w-4 mr-2" />
              View
            </Button>
          </div>
        </CardContent>
      </Card>
      <DocumentViewerDialog
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        url={url}
        fileName={displayName}
        fileType={fileExt}
      />
    </>
  );
}

function LinkContent({ url }: { url: string | null }) {
  if (!url) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Link className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">Link not available</p>
        </CardContent>
      </Card>
    );
  }

  // Try to extract domain for display
  let displayUrl = url;
  try {
    const urlObj = new URL(url);
    displayUrl = urlObj.hostname;
  } catch {
    // If URL parsing fails, just show the full URL
  }

  return (
    <Card>
      <CardContent className="py-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link className="h-10 w-10 text-primary" />
            <div>
              <p className="font-medium">External Resource</p>
              <p className="text-sm text-muted-foreground truncate max-w-md">{displayUrl}</p>
            </div>
          </div>
          <Button asChild variant="outline">
            <a href={url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Link
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AudioContent({ url, displayTitle }: { url: string | null; displayTitle?: string | null }) {
  if (!url) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Music className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">Audio not available</p>
        </CardContent>
      </Card>
    );
  }

  // Extract filename from URL for fallback
  const urlFileName = url.split('/').pop()?.split('?')[0] || 'Audio';
  const displayName = displayTitle || urlFileName.replace(/\.[^/.]+$/, "");

  return (
    <Card>
      <CardContent className="py-6">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Music className="h-10 w-10 text-primary" />
            <div>
              <p className="font-medium">{displayName}</p>
              <p className="text-sm text-muted-foreground">Audio</p>
            </div>
          </div>
          <audio src={url} controls className="w-full">
            Your browser does not support the audio element.
          </audio>
        </div>
      </CardContent>
    </Card>
  );
}
